import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export interface SalesMarket {
  id: number;
  market_name: string;
  market_code: string;
  display_name: string;
  synonyms: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch all active sales markets
 */
export function useSalesMarkets() {
  return useQuery<SalesMarket[]>({
    queryKey: ['sales-markets'],
    queryFn: async () => {
      const response = await apiClient.get('/sales-markets');
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - markets don't change often
  });
}

/**
 * Fetch all sales markets including inactive (admin only)
 */
export function useAllSalesMarkets() {
  return useQuery<SalesMarket[]>({
    queryKey: ['sales-markets', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/sales-markets/all');
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Fetch just the market codes (useful for dropdowns)
 */
export function useSalesMarketCodes() {
  return useQuery<string[]>({
    queryKey: ['sales-markets', 'codes'],
    queryFn: async () => {
      const response = await apiClient.get('/sales-markets/codes');
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Fetch markets as code -> name mapping
 */
export function useSalesMarketMap() {
  return useQuery<Record<string, string>>({
    queryKey: ['sales-markets', 'map'],
    queryFn: async () => {
      const response = await apiClient.get('/sales-markets/map');
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Fetch a specific market by code
 */
export function useSalesMarket(code: string) {
  return useQuery<SalesMarket>({
    queryKey: ['sales-markets', code],
    queryFn: async () => {
      const response = await apiClient.get(`/sales-markets/${code}`);
      return response.data;
    },
    enabled: !!code,
    staleTime: 1000 * 60 * 60,
  });
}
